import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
} from '@/components/ui/kibo-ui/combobox';
import { cn } from '@/lib/utils';
import { ChevronsUpDownIcon } from 'lucide-react';
import { useState } from 'react';
import { useI18n } from '@/providers/i18n';
import { usePreviewCanvas } from '@/hooks/use-preview-canvas';

export type SimpleOption = { value: string; label: string; keywords?: string[] };

type SimpleSelectItemProps = {
  id: string;
  value: string;
  options: SimpleOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  width?: number | string | 'auto';
  className?: string;
  placeholder?: string;
  /** 是否显示搜索框；默认在选项较多（>8）时自动开启 */
  searchable?: boolean;
};

export function SimpleSelectItem({
  id,
  value,
  options,
  onChange,
  disabled,
  width = 'auto',
  className,
  placeholder,
  searchable,
}: SimpleSelectItemProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const isPreview = usePreviewCanvas();
  const effectiveDisabled = Boolean(disabled || isPreview);
  const showSearch = searchable ?? options.length > 8;

  return (
    <Combobox
      open={open}
      onOpenChange={(next) => {
        if (effectiveDisabled) return;
        setOpen(next);
      }}
      data={options}
      type="option"
      value={value}
      onValueChange={(val) => {
        onChange(val);
        setOpen(false);
      }}
    >
      <ComboboxTrigger
        id={id}
        variant="ghost"
        className={cn('rounded-xl h-9 px-3 hover:bg-sidebar-accent transition-all border-none font-medium', className)}
        style={width === 'auto' ? {} : { width }}
        disabled={effectiveDisabled}
      >
        <div className="flex items-center gap-2 w-full">
          <span className="block truncate min-w-0 flex-1 text-center">
            {options.find((o) => o.value === value)?.label ?? (placeholder ?? t('Select'))}
          </span>
          <ChevronsUpDownIcon size={14} className="shrink-0 text-muted-foreground opacity-50" />
        </div>
      </ComboboxTrigger>
      <ComboboxContent className="p-0" popoverOptions={{ className: 'fm-toolbar-surface rounded-2xl border border-sidebar-border bg-sidebar/95 backdrop-blur-xl p-1.5 shadow-none' }}>
        {showSearch ? <ComboboxInput className="h-9" /> : null}
        <ComboboxList className="custom-scrollbar">
          <ComboboxEmpty>{t('No options')}</ComboboxEmpty>
          <ComboboxGroup className="p-1">
            {options.map((opt) => (
              <ComboboxItem
                key={opt.value}
                value={opt.value}
                keywords={[opt.label, ...(opt.keywords ?? [])]}
                className="rounded-lg py-2 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer gap-2"
              >
                <span className="block truncate leading-tight font-medium">{opt.label}</span>
              </ComboboxItem>
            ))}
          </ComboboxGroup>
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  );
}


